import { Directive, Signal, WritableSignal, computed, effect, input, output, signal } from '@angular/core';
import { CoreComponent } from '../core/core.component.js';

export type FieldDesign = 'default' | 'alt';

@Directive()
export abstract class FormFieldComponent<TValueType> extends CoreComponent {
	public readonly title = input<string>();
	public readonly subtitle = input<string>();
	public readonly isRequired = input<boolean>(false);
	public readonly valueAsSignal = input<WritableSignal<TValueType | undefined>>();
	public readonly placeholder = input<string>('');
	public readonly isInDialog = input<boolean>(false);
	public readonly fieldDesign = input<FieldDesign>('default');
	public readonly suffix = input<string>();

	public readonly valueChange = output<TValueType | undefined>();
	public readonly requiredValueChange = output<TValueType>();

	public readonly inputDesignClass = computed<'field-design-alt' | 'field-design-default'>(() => {
		if (this.isInDialog() || this.fieldDesign() === 'alt') {
			return 'field-design-alt';
		}
		return 'field-design-default';
	});

	public readonly isValid: Signal<boolean> = computed(() => {
		return this.calculateIsValid(this.rawValue());
	});

	public readonly hint: Signal<string | undefined> = computed(() => {
		return this.getHint(this.rawValue());
	});

	/**
	 * Do not focus on mobiles screen because it triggers keyboard to show
	 */
	protected readonly canFocusField = computed<boolean>(() => !this.isMobileView());

	public readonly rawValue: WritableSignal<TValueType | undefined> = signal(undefined);
	public readonly lastValidValue: WritableSignal<TValueType | undefined> = signal(undefined);
	public readonly currentValue: WritableSignal<TValueType | undefined> = signal(undefined);

	constructor() {
		super();

		effect(() => {
			const valueSignal = this.valueAsSignal();

			if (valueSignal) {
				const value = valueSignal();

				if (this.isValueValid(value)) {
					// update raw + current value + last valid value
					this.rawValue.set(value);
					this.currentValue.set(value);
					this.lastValidValue.set(value);
				} else {
					// update only raw value because current value is "not valid"
					this.rawValue.set(value);
				}
			}
		});
	}

	protected abstract isValueValid(value?: TValueType): boolean;
	protected abstract getHint(value?: TValueType): string | undefined;

	protected updateValue(
		value?: TValueType,
		config?: {
			valueForInvalidValue: TValueType | undefined;
		}
	): void {
		this.rawValue.set(value);

		if (this.isValid()) {
			this.currentValue.set(value);
			this.lastValidValue.set(value);
			this.valueAsSignal?.()?.set(value);
			this.valueChange.emit(value);

			if (value) {
				this.requiredValueChange.emit(value);
			}
		} else {
			const invalidValue: TValueType | undefined = config?.valueForInvalidValue ?? undefined;

			this.currentValue.set(invalidValue);
			this.valueAsSignal?.()?.set(invalidValue);
			this.valueChange.emit(invalidValue);
		}
	}

	private calculateIsValid(value?: TValueType): boolean {
		if (this.isRequired() && !value) {
			return false;
		}
		return this.isValueValid(value);
	}
}
